// Search-field throttle for every list search box (TableSearchBar; flag
// REACT_APP_LIST_SEARCH_THROTTLE_ENABLED - analysis 14/9 §3C, step 3).
// Pure module: no flag reads here, so enviroment.ts can import the defaults
// without a cycle.
//
// Prod facts (14/9): the field sends one GetAllLogos per typing pause
// > 350 ms with no minimum length, 45 % of search->search pairs overlap,
// and the server runs abandoned calls to completion (CommandTimeout
// 30 000 s). A longer window and a minimum of 2 characters cut the number
// of calls; the rows themselves (prices, stock, status) are untouched -
// only WHEN a search is sent changes.

// Today's debounce (TableSearchBar), kept byte for byte while the flag is
// off.
export const LEGACY_SEARCH_DEBOUNCE_MS = 350;
export const DEFAULT_LIST_SEARCH_DEBOUNCE_MS = 700;
export const DEFAULT_LIST_SEARCH_MIN_CHARS = 2;
// Guard rails for the env overrides: a typo must never freeze the field or
// make it unsearchable.
export const MAX_LIST_SEARCH_DEBOUNCE_MS = 5_000;
export const MAX_LIST_SEARCH_MIN_CHARS = 10;

export interface ListSearchThrottle {
  enabled: boolean;
  // Wait after the last keystroke before the term is sent (Enter skips it).
  debounceMs: number;
  // A non-empty term shorter than this is never sent; 0 = no minimum.
  minChars: number;
}

function parseBoundedInt(
  raw: string | undefined,
  fallback: number,
  max: number,
): number {
  if (raw === undefined || raw.trim() === "") return fallback;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= max ? n : fallback;
}

export function parseListSearchDebounceMs(raw: string | undefined): number {
  return parseBoundedInt(
    raw,
    DEFAULT_LIST_SEARCH_DEBOUNCE_MS,
    MAX_LIST_SEARCH_DEBOUNCE_MS,
  );
}

export function parseListSearchMinChars(raw: string | undefined): number {
  return parseBoundedInt(
    raw,
    DEFAULT_LIST_SEARCH_MIN_CHARS,
    MAX_LIST_SEARCH_MIN_CHARS,
  );
}

// Flag off = today's numbers (350 ms, no minimum) whatever the overrides
// say, so an override can never leak into a build with the flag off.
export function resolveListSearchThrottle(
  enabled: boolean,
  debounceMs: number,
  minChars: number,
): ListSearchThrottle {
  return enabled
    ? { enabled: true, debounceMs, minChars }
    : { enabled: false, debounceMs: LEGACY_SEARCH_DEBOUNCE_MS, minChars: 0 };
}

// A non-empty term shorter than the minimum is never sent. The empty term
// (clearing the field) always is - it restores the unfiltered list, which
// is usually a cache hit anyway.
export function isTooShortToSearch(term: string, minChars: number): boolean {
  const len = term.trim().length;
  return len > 0 && len < minChars;
}
